import Tippy from '@tippyjs/react';
import { Placement } from 'popper.js';
import { ReactNode } from 'react';
import type { Instance as TippyInstance } from 'tippy.js';
import styled, { SimpleInterpolation } from 'styled-components';
import { BouColor } from '../../global/bou-colors';
import { uprightTippyStyling } from './tippy-styles/upright';

export interface InfoTooltipProps {
  className?: string;
  tooltipContent: JSX.Element;
  children: ReactNode;
  position?: Placement;
  open?: boolean;
  trigger?: string; // A combination of "mouseenter", "focus", "focusin", "click", "manual"
  /** Tippy show/hide delay in ms. A single number sets both; a tuple sets [show, hide]. Used for
   * delayed-hover tooltips so they don't flash while the pointer skims past the trigger. */
  delay?: number | [number, number];
  enabled?: boolean;
  styleOverride?: SimpleInterpolation;
  visible?: boolean;
  interactive?: boolean;
  fixed?: boolean;
  maxWidth?: string | number | undefined;
  hideOnClick?: boolean | 'toggle';
  arrow?: boolean;
  /** Render tooltip into this element. Use document.body when inside overflow containers (e.g. tables) so the tooltip is not clipped. */
  appendTo?: 'parent' | Element | ((ref: Element) => Element);
  /** Called once with the Tippy instance when created. Lets callers imperatively `show()`/`hide()` —
   * e.g. to reveal on click without inheriting the hover `delay` (delay is ignored by `show()`). */
  onCreate?: (instance: TippyInstance) => void;
}

export function InfoTooltip(props: InfoTooltipProps) {
  const disabled = props.enabled !== undefined && !props.enabled;
  return (
    <StyledTippy
      className={props.className}
      content={
        <StyledWrap $styleOverride={props.styleOverride} maxWidth={props.maxWidth}>
          {props.tooltipContent}
        </StyledWrap>
      }
      trigger={props.visible === undefined ? props.trigger || 'click' : undefined}
      interactive={props.interactive ?? true}
      arrow={props.arrow ?? true}
      delay={props.delay}
      duration={100}
      placement={props.position || 'top'}
      showOnCreate={props.open}
      disabled={disabled}
      visible={props.visible}
      hideOnClick={props.hideOnClick ?? true}
      popperOptions={props.fixed === true ? { strategy: 'fixed' } : undefined}
      appendTo={props.appendTo}
      onCreate={props.onCreate}
    >
      <ChildrenWrap {...{ disabled }}>{props.children}</ChildrenWrap>
    </StyledTippy>
  );
}

interface TippyStyleProps {
  $styleOverride: SimpleInterpolation | undefined;
  maxWidth: string | number | undefined;
}

/**
 * OA 10/2022: Due to a hook error occuring from styled-components 5.0.1 onwards, I had to downgrade it to 5.0.0
 * after updating to React v18.2.0. Unfortunately, styled-components 5.0.0 doesn't properly handle transient props
 * (those starting with a dollar sign), and hence the $styleOverride prop gets passed to Tippy, resulting
 * in a warning. I went around this with the hack below, having a separate styled wrapper div for all the
 * tooltip content, and styling the Tippy itself with `display: contents;`. Seems to work fine.
 */
const StyledTippy = styled(Tippy)`
  display: contents;
`;

const StyledWrap = styled.div<TippyStyleProps>`
  ${uprightTippyStyling}
  > * {
    min-width: 200px; // This ensures the content stays readable, since otherwise it'll be much too narrow if width isn't set explicitly on a component using InfoToolTip.
  }
  max-width: ${props => (props.maxWidth ? props.maxWidth : '100vw !important')};
  padding-left: 24px !important;
  padding-right: 24px !important;
  padding-top: 18px !important;
  padding-bottom: 18px !important;
  line-height: 22px;
  color: ${BouColor.TOOL_TEXT_BLACK};
  font-size: 14px;
  text-align: left;
  &:focus {
    outline: none;
  }
  ${props => props.$styleOverride}
`;

/*
  Need to have the styled component to make children work. See more at
  https://github.com/atomiks/tippy.js-react or read below:
  "If you want to use a component element as a child, ensure you forward the ref to the DOM node...
  ...styled-components v4 does this for you automatically, so it should be seamless when using the styled constructor."

  NOTE: This wrapper is for the content that opens the tooltip, not the tooltip content itself.
*/
const ChildrenWrap = styled.span<{ disabled: boolean }>`
  text-align: left;
  :focus {
    outline: none;
  }
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
`;
